import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { DeleteAction } from "@/components/table/actions";

import { useUploadProjectAttachmentMutation } from "@/services/(company)/api.projects";

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useCallback, useState, type ReactNode } from "react";
import { useDropzone } from "react-dropzone";
import * as yup from "yup";
import { useFormik } from "formik";
import { FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

import { useParams } from "react-router-dom";
import toast from "react-hot-toast";
import type { projectIdKey } from "@/routes/company/routes.projects.constants";

const buildFormSchema = (t: TFunction) =>
  yup.object({
    name: yup.string().required(t("projects:name_is_required")),
    description: yup.string(),
    attachments: yup
      .mixed<File>()
      .nullable()
      .required(t("validation:file_required")),
  });

type Fields = yup.InferType<ReturnType<typeof buildFormSchema>>;

const fieldNames: Record<keyof Fields, keyof Fields> = {
  name: "name",
  description: "description",
  attachments: "attachments",
};

export const UploadProjectAttachmentDialog = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { t } = useTranslation(["projects", "common", "validation"]);
  const [isOpen, setIsOpen] = useState(false);
  const { projectId } = useParams<{ [projectIdKey]: string }>();
  const [uploadAttachment, { isLoading }] =
    useUploadProjectAttachmentMutation();

  const onOpenChange = (open: boolean) => {
    setIsOpen(open);
    formik.resetForm();
  };

  const formik = useFormik<Fields>({
    initialValues: {
      name: "",
      description: "",
      attachments: null as unknown as File,
    },
    validationSchema: buildFormSchema(t),
    onSubmit: async (values) => {
      const payload = new FormData();
      payload.append(fieldNames.attachments, values.attachments as File);
      payload.append(fieldNames.name, values.name);
      payload.append(fieldNames.description, values.description || "");

      if (!projectId) return toast.error(t("projects:project_not_found"));
      await uploadAttachment({ projectId: projectId, payload })
        .unwrap()
        .then((res) => {
          toast.success(res.message);
          onOpenChange(false);
        })
        .catch((e) => toast.error(e?.data?.message ?? e?.message));
    },
  });

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles?.length > 0) {
        formik.setFieldValue(fieldNames.attachments, acceptedFiles[0]);
        // Auto-fill name field if empty
        if (!formik.values.name)
          formik.setFieldValue(fieldNames.name, acceptedFiles[0].name);
      }
    },
    [formik],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected: () => {
      toast.error(
        t("projects:file_too_large_or_file_type_not_supported_please_upload_"),
      );
    },
    multiple: false,
    maxFiles: 1,
    accept: {
      "image/*": [],
      "application/pdf": [".pdf"],
    },
  });

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-sm">
        {/* FIX: Only one form element, wrapping the content */}
        <form onSubmit={formik.handleSubmit}>
          <DialogHeader>
            <DialogTitle>{t("projects:upload_attachment")}</DialogTitle>
          </DialogHeader>

          <div className="py-4">
            {formik.values.attachments ? (
              <div className="w-full p-4 rounded-sm border-2 border-dashed border-primary bg-primary/10 flex items-center justify-between">
                <a
                  target="_blank"
                  href={URL.createObjectURL(formik.values.attachments)}
                  className="max-w-max text-sm font-medium truncate flex gap-2 me-2 text-primary hover:underline"
                >
                  {(formik.values.attachments as File).name}
                </a>
                <DeleteAction
                  onClick={() =>
                    formik.setFieldValue(fieldNames.attachments, null)
                  }
                  type="button"
                  title={t("projects:remove_attachment")}
                  className="h-8 w-8"
                />
              </div>
            ) : (
              <div
                {...getRootProps()}
                className={`p-10 min-h-40 border-2 border-dashed rounded-md text-center cursor-pointer transition-all grid place-items-center
                  ${isDragActive ? "border-primary bg-primary/5" : "border-muted-foreground/25 hover:border-primary/50"}`}
              >
                <input {...getInputProps()} />
                <p className="text-sm text-muted-foreground">
                  {isDragActive
                    ? t("projects:drop_it_here")
                    : t("projects:drag_and_drop_or_click_to_select")}
                </p>
              </div>
            )}
          </div>

          <div className="space-y-4 mb-6">
            <div className="grid gap-2">
              <FieldLabel htmlFor={fieldNames.name}>
                {t("projects:document_title")}
              </FieldLabel>
              <Input
                id={fieldNames.name}
                name={fieldNames.name}
                value={formik.values.name}
                onChange={formik.handleChange}
                placeholder={t("projects:document_name")}
              />
            </div>
            <div className="grid gap-2">
              <FieldLabel htmlFor={fieldNames.description}>
                {t("projects:description")}
              </FieldLabel>
              <Textarea
                id={fieldNames.description}
                name={fieldNames.description}
                value={formik.values.description}
                onChange={formik.handleChange}
                placeholder={t("projects:describe_what_this_document_is_for")}
              />
            </div>
          </div>

          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" type="button">
                {t("common:buttons.cancel")}
              </Button>
            </DialogClose>
            <Button
              type="submit"
              disabled={isLoading || !formik.values.attachments}
            >
              {isLoading ? t("projects:uploading") : t("projects:upload")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
